import type { CSSProperties, FunctionComponent, HTMLAttributes, ReactElement } from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { DrawerDragHandle } from '@pp/core/src/components/game/drawer/drawerDragHandle/DrawerDragHandle';
import { useDrawerDrag } from '@pp/core/src/components/game/drawer/useDrawerDrag';
import { useDrawerExpandable } from '@pp/core/src/components/game/drawer/useDrawerExpandable';
import { useMaxKeyboardAwareDrawerHeight } from '@pp/core/src/components/game/drawer/useMaxKeyboardAwareDrawerHeight';
import { PopupType } from '@pp/core/src/components/popup/types';
import { Logger } from '@pp/core/src/logger/Logger';
import { useModifiers } from '@pp/core/src/styles/hooks/useModifiers';

import { launcherContextActions } from '../../context/actions';
import { useLauncherContext } from '../../context/LauncherContext';
import { GAME_POPUPS } from '../../context/types';
import { LauncherCommunicator } from '../../controllers/LauncherCommunicationController';

import styles from './Drawer.module.css';

export interface DrawerProps extends Omit<HTMLAttributes<HTMLDivElement>, ''> {
  open: boolean;
  className?: string;
  end?: boolean;
  onClose?: () => void;
  wide?: boolean;
  hideHeader?: boolean;
  drawerContentClassName?: string;
  gameStyle?: boolean;
  expandable?: boolean;
  isForcedExpanded?: boolean;
  anchorOrigin?: 'start' | 'end';
  minimize?: boolean;
  mini?: boolean;
  shouldStayAtTable?: boolean;
}

export interface DrawerWindowProps extends DrawerProps {
  anchor: { x: number; y: number; width: number } | null;
}

const DrawerWindow: FunctionComponent<DrawerWindowProps> = ({
  anchor,
  anchorOrigin = 'start',
  children,
  className = '',
  end,
  open,
  wide,
  drawerContentClassName = '',
  hideHeader = false,
  gameStyle = false,
  expandable = false,
  isForcedExpanded = false,
  minimize = false,
  shouldStayAtTable,
  ...rest
}) => {
  const [shouldRender, setShouldRender] = useState(open);
  const drawerRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setExpanded] = useState(false);
  const maxKeyboardAwareHeight = useMaxKeyboardAwareDrawerHeight();
  const [drawerWidth, setDrawerWidth] = useState<number>(4);
  const shouldExpand = useDrawerExpandable(expandable);
  const { tables, dispatch } = useLauncherContext();
  const [isMinimizedState, setIsMinimizedState] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [keepFullHeight, setKeepFullHeight] = useState(false);
  const previousExpandedRef = useRef(false);

  const mtbTable = tables[1];
  const isMinimised = mtbTable?.isMtbMinimized ?? false;

  useEffect(() => {
    if (isMinimised !== isMinimizedState) {
      setIsTransitioning(true);
      setIsMinimizedState(isMinimised);
      const timer = setTimeout(() => {
        setIsTransitioning(false);
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [isMinimised, isMinimizedState]);

  const handleClose = (): void => {
    if (open) {
      if (isExpanded || isForcedExpanded) {
        setKeepFullHeight(true);
      } else {
        setExpanded(false);
      }

      LauncherCommunicator.dispatchShowPopup(
        shouldStayAtTable ? PopupType.DontLeaveRoundInProgress : GAME_POPUPS.LEAVE_BACCARAT_MULTIPLAY
      );
    }
  };

  const handleMinimize = (): void => {
    setIsTransitioning(true);
    dispatch(launcherContextActions.toggleMtb({ isMinimize: !isMinimised }));
    setTimeout(() => {
      setIsTransitioning(false);
    }, 300);
  };

  const { delta, dragRef, isDragging } = useDrawerDrag({
    onClose: handleMinimize,
    isExpanded,
    setExpanded,
    open: open && !isMinimised, 
  });

  useLayoutEffect(() => {
    if (open) {
      drawerRef.current?.classList.remove(styles.drawer_closing);
      setShouldRender(true);
    } else {
      drawerRef.current?.classList.add(styles.drawer_closing);
    }
  }, [open]);

  useEffect(() => {
    if (!shouldRender) return;
    const updateAnchor = (): void => {
      setDrawerWidth(drawerRef.current?.getBoundingClientRect().width ?? 0);
    };

    const updateAnchorEventCallback = (): void => {
      window.setTimeout(() => {
        updateAnchor();
      });
    };

    updateAnchor();
    window.addEventListener('orientationchange', updateAnchorEventCallback, { passive: true });
    window.addEventListener('resize', updateAnchorEventCallback, { passive: true });

    return (): void => {
      window.removeEventListener('resize', updateAnchorEventCallback);
      window.removeEventListener('orientationchange', updateAnchorEventCallback);
    };
  }, [shouldRender]);

  useEffect(() => {
    if (!isMinimised) {
      setKeepFullHeight(false);
      previousExpandedRef.current = true;
    }
  }, [isMinimised]);

  const onAnimationEndHandle = (): void => {
    if (!open) {
      setShouldRender(false);
    }
    setIsTransitioning(false);
  };

  const drawerWithMods = useModifiers(
    styles,
    'drawer',
    {
      end,
      wide,
      default: !gameStyle,
      expandable: shouldExpand,
      minimized: isMinimised,
      expanded: isExpanded,
      forcedExpanded: isForcedExpanded,
      fullHeightOnClose: keepFullHeight,
      isDragging,
      open,
      transitioning: isTransitioning,
    },
    { withBaseClass: true }
  );

  if (!shouldRender) {
    return null;
  }

  const root = document.getElementById('drawerRoot');
  if (!root) {
    Logger.logFailure('drawer', {
      additional: 'noRoot',
    });
    return null;
  }

  return createPortal(
    <div
      className={drawerWithMods}
      style={
        {
          '--anchorOffset': anchor
            ? anchorOrigin === 'end'
              ? `translate(calc(${anchor.x + anchor.width - drawerWidth}px), ${anchor.y}px)`
              : `translate(calc(${anchor.x}px), ${anchor.y}px)`
            : '',
          '--offset-y': `${anchor?.y ?? 0}px`,
          '--delta': `${delta ?? 0}px`,
          '--maxHeight': maxKeyboardAwareHeight ? `${maxKeyboardAwareHeight}px` : '100%',
          opacity: isMinimised ? 0 : 1,
          zIndex: isMinimised ? -1 : 1,
          pointerEvents: isMinimised ? 'none' : 'auto',
        } as CSSProperties
      }
      ref={drawerRef}
      onAnimationEnd={onAnimationEndHandle}
    >
      <div className={styles.drawer__overlay} onClick={handleClose} />
      <div className={`${styles.drawer__wrapper} ${className}`}>
        <div data-testid="drawer" className={styles.drawer__body} {...rest}>
        {shouldExpand || isExpanded ? ( 
          <div className={styles.drawer__dragHandleWrapper}>
            <DrawerDragHandle ref={dragRef} />
          </div>
        ) : null}
          <div className={`${styles.drawer__content} ${drawerContentClassName}`}>{children}</div>
        </div>
      </div>
    </div>,
    root
  );
};

export const Drawer: FunctionComponent<DrawerProps> = ({ open, end, ...rest }) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [anchor, setAnchor] = useState<{ x: number; y: number; width: number } | null>(null);

  useEffect(() => {
    if (!open) return;
    const updateAnchor = (): void => {
      if (anchorRef.current) {
        const rect = anchorRef.current.getBoundingClientRect();
        setAnchor({
          x: end ? -window.innerWidth + rect.x : rect.x,
          y: -window.innerHeight + rect.y,
          width: rect.width,
        });
      }
    };

    const updateAnchorEventCallback = (): void => {
      window.setTimeout(() => {
        updateAnchor();
      });
    };

    updateAnchor();
    window.addEventListener('orientationchange', updateAnchorEventCallback, { passive: true });
    window.addEventListener('resize', updateAnchorEventCallback, { passive: true });

    return (): void => {
      window.removeEventListener('resize', updateAnchorEventCallback);
      window.removeEventListener('orientationchange', updateAnchorEventCallback);
    };
  }, [end, open]);

  return (
    <>
      <DrawerWindow open={open} end={end} anchor={anchor} {...rest} />
      <div className={styles.drawer__anchor} ref={anchorRef} />
    </>
  );
};
